// An event that repeats, the same for the board's own events and Google's (Calendar plan, step 3): the rule as the
// repeat editor sets it (every day, weekdays, every week on chosen days, every month, every year; every so many; until
// a day or for a number of times), Google's RRULE for it and back, the rule in words, and each time it happens between
// two days. A rule the editor cannot show (a Google repeat made with more parts) is kept as Google has it and edited
// in Google.
//
// The board's own small reader rather than a library: the editor offers few rules, and these lines do every one of
// them, with nothing more to carry in the page.

import { addDays, cleanDay, daysBetween, weekdayOf } from "./days";

export type Freq = "daily" | "weekly" | "monthly" | "yearly";
export const FREQS: Freq[] = ["daily", "weekly", "monthly", "yearly"];

export interface RepeatRule {
  freq: Freq;
  /** every so many days, weeks, months or years; 1 when left out */
  every?: number;
  /** weekly: the weekdays it happens on, 0 Sunday to 6 Saturday; its first day's weekday when left out */
  days?: number[];
  /** the last day it may happen on, YYYY-MM-DD */
  until?: string;
  /** or how many times it happens */
  count?: number;
  /** the days it was taken out on (one changed or deleted on its own), by the day each would have started */
  skip?: string[];
}

export class RepeatRefused extends Error {}
const refuse = (m: string): never => {
  throw new RepeatRefused(m);
};

/** The longest run worked out at once, so no rule can keep a page busy. */
const MAX_STEPS = 5000;

/** A rule as it may be kept: a known frequency, sensible numbers, real days; null for none. */
export function cleanRepeat(x: unknown): RepeatRule | null {
  if (x === null || x === undefined || x === false) return null;
  if (typeof x !== "object" || Array.isArray(x)) return refuse("A repeat is {freq, every, days, until, count}, or null.");
  const r = x as Record<string, unknown>;
  const freq = FREQS.includes(r.freq as Freq) ? (r.freq as Freq) : refuse(`A repeat's freq is daily, weekly, monthly or yearly, not "${String(r.freq)}".`);
  const every = r.every === undefined || r.every === null ? 1 : Number.isInteger(r.every) && (r.every as number) >= 1 && (r.every as number) <= 99 ? (r.every as number) : refuse("A repeat's every is a whole number from 1 to 99.");
  const days = Array.isArray(r.days) ? [...new Set(r.days.filter((d): d is number => Number.isInteger(d) && (d as number) >= 0 && (d as number) <= 6))].sort((a, b) => a - b) : [];
  const until = r.until === undefined || r.until === null ? null : (cleanDay(r.until) ?? refuse(`A repeat's until is a day like 2026-12-31, not "${String(r.until)}".`));
  const count = r.count === undefined || r.count === null ? null : Number.isInteger(r.count) && (r.count as number) >= 1 && (r.count as number) <= 999 ? (r.count as number) : refuse("A repeat's count is a whole number from 1 to 999.");
  if (until && count) refuse("A repeat ends on a day or after a number of times, not both.");
  const skip = Array.isArray(r.skip) ? [...new Set(r.skip.map((d) => cleanDay(d)).filter((d): d is string => !!d))].sort().slice(-500) : [];
  return { freq, ...(every > 1 ? { every } : {}), ...(freq === "weekly" && days.length ? { days } : {}), ...(until ? { until } : {}), ...(count ? { count } : {}), ...(skip.length ? { skip } : {}) };
}

export const pad = (n: number) => String(n).padStart(2, "0");
const monthsBetween = (a: string, b: string) => (+b.slice(0, 4) - +a.slice(0, 4)) * 12 + (+b.slice(5, 7) - +a.slice(5, 7));
const daysIn = (y: number, m: number) => new Date(Date.UTC(y, m, 0)).getUTCDate();

/**
 * The days a rule happens on, from its first day, that fall between from and to (to included), in order. count counts
 * from the first day, skipped days among them (a day taken out still used up its turn, as calendars count it).
 */
export function repeatDays(rule: RepeatRule, first: string, from: string, to: string): string[] {
  const out: string[] = [];
  const every = rule.every ?? 1;
  const last = rule.until && rule.until < to ? rule.until : to;
  if (last < first) return out;
  const skip = new Set(rule.skip ?? []);
  let n = 0;
  const take = (d: string): boolean => {
    if (d < first) return true;
    if (d > last) return false;
    n++;
    if (rule.count && n > rule.count) return false;
    if (d >= from && !skip.has(d)) out.push(d);
    return true;
  };
  if (rule.freq === "daily") {
    // jump near from, keeping to the rule's step, when no count needs each step counted
    let start = 0;
    if (!rule.count && from > first) start = Math.floor(daysBetween(first, from) / every);
    for (let i = start, guard = 0; guard < MAX_STEPS; i++, guard++) if (!take(addDays(first, i * every))) break;
  } else if (rule.freq === "weekly") {
    const days = rule.days?.length ? rule.days : [weekdayOf(first)];
    const week0 = addDays(first, -weekdayOf(first));
    let start = 0;
    if (!rule.count && from > first) start = Math.max(0, Math.floor(daysBetween(week0, from) / 7 / every) - 1);
    outer: for (let w = start, guard = 0; guard < MAX_STEPS; w++, guard++) {
      const sunday = addDays(week0, w * 7 * every);
      if (sunday > last) break;
      for (const wd of days) if (!take(addDays(sunday, wd))) break outer;
    }
  } else {
    const y0 = +first.slice(0, 4);
    const m0 = +first.slice(5, 7);
    const d0 = +first.slice(8, 10);
    const stepMonths = rule.freq === "monthly" ? every : 12 * every;
    let start = 0;
    if (!rule.count && from > first) start = Math.max(0, Math.floor(monthsBetween(first, from) / stepMonths) - 1);
    for (let i = start, guard = 0; guard < MAX_STEPS; i++, guard++) {
      const months = m0 - 1 + i * stepMonths;
      const y = y0 + Math.floor(months / 12);
      const m = (months % 12) + 1;
      if (`${y}-${pad(m)}-01` > last) break;
      // a month (or a year) without that day is passed over, and not counted, as calendars do: no 31st of June
      if (d0 > daysIn(y, m)) continue;
      if (!take(`${y}-${pad(m)}-${pad(d0)}`)) break;
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Google's RRULE

const BYDAY = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];

/** Google's recurrence lines for a rule: one RRULE (and its skipped days, which Google keeps as instances it cancelled). */
export function toRRule(rule: RepeatRule, opts: { allDay: boolean; tzUntil?: (day: string) => string }): string[] {
  const parts = [`FREQ=${rule.freq.toUpperCase()}`];
  if ((rule.every ?? 1) > 1) parts.push(`INTERVAL=${rule.every}`);
  if (rule.freq === "weekly" && rule.days?.length) parts.push(`BYDAY=${rule.days.map((d) => BYDAY[d]).join(",")}`);
  if (rule.count) parts.push(`COUNT=${rule.count}`);
  // until: the end of that day; for a timed event Google wants it as a moment in UTC
  if (rule.until) parts.push(`UNTIL=${opts.allDay ? rule.until.replace(/-/g, "") : (opts.tzUntil?.(rule.until) ?? `${rule.until.replace(/-/g, "")}T235959Z`)}`);
  return [`RRULE:${parts.join(";")}`];
}

/**
 * A rule from Google's recurrence lines, when the editor can show it; custom when it has parts the editor does not
 * offer (the second Tuesday of the month, several RRULEs), which are kept as Google has them and changed in Google.
 */
export function fromRRule(lines: unknown, tzOfUntil?: (utc: string) => string): { rule: RepeatRule | null; custom: boolean } {
  const list = Array.isArray(lines) ? lines.filter((l): l is string => typeof l === "string") : [];
  const rules = list.filter((l) => /^RRULE:/i.test(l));
  if (!rules.length) return { rule: null, custom: list.length > 0 };
  if (rules.length > 1 || list.some((l) => /^(RDATE|EXRULE):/i.test(l))) return { rule: null, custom: true };
  const kv = Object.fromEntries(rules[0].slice(6).split(";").map((p) => p.split("=") as [string, string]).map(([k, v]) => [k.toUpperCase(), v ?? ""]));
  const known = new Set(["FREQ", "INTERVAL", "BYDAY", "COUNT", "UNTIL", "WKST"]);
  if (Object.keys(kv).some((k) => !known.has(k))) return { rule: null, custom: true };
  const freq = String(kv.FREQ ?? "").toLowerCase() as Freq;
  if (!FREQS.includes(freq)) return { rule: null, custom: true };
  const days = kv.BYDAY ? kv.BYDAY.split(",").map((d) => BYDAY.indexOf(d.toUpperCase())) : [];
  // "the first Monday" (1MO) or days on a monthly rule: more than the editor offers
  if (days.some((d) => d < 0) || (days.length && freq !== "weekly")) return { rule: null, custom: true };
  let until: string | undefined;
  if (kv.UNTIL) {
    const m = /^(\d{4})(\d{2})(\d{2})(T\d{6}Z?)?$/.exec(kv.UNTIL);
    if (!m) return { rule: null, custom: true };
    until = m[4] && tzOfUntil ? tzOfUntil(kv.UNTIL) : `${m[1]}-${m[2]}-${m[3]}`;
  }
  try {
    return { rule: cleanRepeat({ freq, ...(kv.INTERVAL ? { every: Number(kv.INTERVAL) } : {}), ...(days.length ? { days } : {}), ...(kv.COUNT ? { count: Number(kv.COUNT) } : {}), ...(until ? { until } : {}) }), custom: false };
  } catch {
    return { rule: null, custom: true };
  }
}

// ---------------------------------------------------------------------------
// in words

const WD = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const short = (d: string, dmy = false) => (dmy ? `${+d.slice(8, 10)} ${MON[+d.slice(5, 7) - 1]} ${d.slice(0, 4)}` : `${MON[+d.slice(5, 7) - 1]} ${+d.slice(8, 10)}, ${d.slice(0, 4)}`);

/** Whether a weekly rule is every weekday, Monday to Friday. */
export const isWeekdays = (r: RepeatRule) => r.freq === "weekly" && (r.every ?? 1) === 1 && (r.days ?? []).join() === "1,2,3,4,5";

/** "Every day", "Every weekday", "Every 2 weeks on Mon, Wed", "Every month on the 30th", "Every year, 5 times", "…, until Dec 31, 2026" (dmy: "31 Dec 2026", as the UK writes it). */
export function repeatWords(rule: RepeatRule, first: string, dmy = false): string {
  const every = rule.every ?? 1;
  const unit = { daily: "day", weekly: "week", monthly: "month", yearly: "year" }[rule.freq];
  let s: string;
  if (isWeekdays(rule)) s = "Every weekday";
  else s = every === 1 ? `Every ${unit}` : `Every ${every} ${unit}s`;
  if (rule.freq === "weekly" && !isWeekdays(rule)) s += ` on ${(rule.days?.length ? rule.days : [weekdayOf(first)]).map((d) => WD[d]).join(", ")}`;
  if (rule.freq === "monthly") {
    const d = +first.slice(8, 10);
    s += ` on the ${d}${d % 10 === 1 && d !== 11 ? "st" : d % 10 === 2 && d !== 12 ? "nd" : d % 10 === 3 && d !== 13 ? "rd" : "th"}`;
  }
  if (rule.freq === "yearly") s += dmy ? ` on ${+first.slice(8, 10)} ${MON[+first.slice(5, 7) - 1]}` : ` on ${MON[+first.slice(5, 7) - 1]} ${+first.slice(8, 10)}`;
  if (rule.count) s += `, ${rule.count} time${rule.count === 1 ? "" : "s"}`;
  if (rule.until) s += `, until ${short(rule.until, dmy)}`;
  return s;
}
